// src/features/business-functions/services/accountService.js
import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';

/**
 * Pure Data Access Layer for the Account Detail page.
 * These functions ONLY handle network requests. They do not touch React state.
 */
export const accountService = {
  /**
   * Fetch a single account's detail by ID.
   * @param {string} accountId
   */
  async getAccountDetail(accountId) {
    if (!accountId) throw new Error('Account ID is required');
    return APIService.get(`${Apis.getAccountsList}${accountId}/`);
  },

  /**
   * Fetch overview stats / analytics for an account.
   * Falls back gracefully when the stats endpoint is not yet available.
   * @param {string} accountId
   * @param {{ period?: string }} [filters]
   */
  async getAccountStats(accountId, filters = {}) {
    if (!accountId) throw new Error('Account ID is required');
    const params = new URLSearchParams();
    if (filters.period) params.set('period', filters.period);
    const query = params.toString();
    const url = query
      ? `${Apis.getAccountsList}${accountId}/stats/?${query}`
      : `${Apis.getAccountsList}${accountId}/stats/`;
    return APIService.get(url);
  },

  /**
   * Fetch workspaces belonging to an account.
   * @param {string} accountId
   * @param {{ search?: string, page?: number }} [filters]
   */
  async getAccountWorkspaces(accountId, filters = {}) {
    if (!accountId) throw new Error('Account ID is required');
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('next_page', String(filters.page));
    const query = params.toString();
    const url = query
      ? `${Apis.getAccountsList}${accountId}/workspaces/?${query}`
      : `${Apis.getAccountsList}${accountId}/workspaces/`;
    return APIService.get(url);
  },

  /**
   * Update account details by ID.
   * @param {string} accountId
   * @param {Object} accountData
   */
  async updateAccount(accountId, accountData) {
    if (!accountId) throw new Error('Account ID is required');
    return APIService.put(`${Apis.getAccountsList}${accountId}/`, accountData);
  },

  /**
   * Create a business function (account) for an organisation.
   * @param {{ name: string, organisation: string, contacts?: Array<object> }} payload
   */
  async createAccount(payload) {
    if (!payload?.name?.trim()) throw new Error('Business function name is required');
    if (!payload?.organisation) throw new Error('Organisation is required');
    return APIService.post(Apis.getAccountsList, {
      ...payload,
      contacts: payload.contacts ?? [],
    });
  },

  /**
   * Activate or deactivate a business function. Platform super admin only.
   * @param {string} accountId
   * @param {boolean} isActive
   */
  async setAccountActive(accountId, isActive) {
    if (!accountId) throw new Error('Account ID is required');
    return APIService.patch(`${Apis.getAccountsList}${accountId}/status/`, {
      is_active: Boolean(isActive),
    });
  },

  /**
   * Enable or disable a business service. Platform super admin only.
   * @param {string} accountId
   * @param {string} proposalId
   * @param {boolean} isDisabled
   */
  async setWorkspaceDisabled(accountId, proposalId, isDisabled) {
    if (!accountId) throw new Error('Account ID is required');
    if (!proposalId) throw new Error('Business service ID is required');
    return APIService.patch(
      `${Apis.getAccountsList}${accountId}/workspaces/${proposalId}/status/`,
      { is_disabled: Boolean(isDisabled) }
    );
  },
};
